import type { ProductDetailAttribute } from '~/types/enrichment';
import { formatProductHighlights } from '~/utils/product-highlights';

/** Curation columns rendered as plain-text "Current" values — mirrors
 *  `buildRawFields` in `curation-products.mapper.ts`, minus the columns
 *  (`custom_labels`, `extra_attributes`) no enrichment field maps back to. */
const PLAIN_STRING_FIELDS = [
  'title',
  'description',
  'brand',
  'color',
  'gender',
  'material',
  'pattern',
  'age_group',
  'google_product_category',
] as const;

/** Result of reading one archived (or live) curation version's flat field map:
 *  a display-ready string per plain field, plus structured `product_details`
 *  attributes kept as data (not re-stringified and re-parsed). */
export interface ParsedVersionFields {
  rawFields: Record<string, string>;
  productDetails?: ProductDetailAttribute[];
}

/** True when every element of an unknown array is a string. */
function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

/**
 * Converts one version's flat content-column map — `ProductVersionSnapshot.fields`
 * (archived) or a live `CurationProductDetailWire`-shaped object — into the
 * "Current" values the compare view's attribute rows read. Kept separate from
 * `curation-products.mapper.ts`'s `buildRawFields` because that one targets the
 * live detail wire specifically; this accepts the more loosely-typed version
 * snapshot map (`Record<string, unknown>`) the versions endpoint returns and
 * skips the lossy generic array/object stringifier for `product_details` —
 * structured attributes are read directly into `productDetails` instead of
 * round-tripping through a joined string.
 */
export function parseVersionSnapshotFields(fields: Record<string, unknown>): ParsedVersionFields {
  const rawFields: Record<string, string> = {};
  for (const key of PLAIN_STRING_FIELDS) {
    const value = fields[key];
    if (typeof value === 'string' && value.trim() !== '') {
      rawFields[key] = value.trim();
    }
  }

  const sizes = fields.sizes;
  if (isStringArray(sizes) && sizes.length > 0) {
    rawFields.sizes = sizes.join(', ');
  }
  const productTypes = fields.product_types;
  if (isStringArray(productTypes) && productTypes.length > 0) {
    rawFields.product_types = productTypes.join(', ');
  }
  const highlights = fields.product_highlights;
  if (isStringArray(highlights) || typeof highlights === 'string') {
    const formatted = formatProductHighlights(highlights);
    if (formatted) rawFields.product_highlights = formatted;
  }

  // `product_details` is an array of `{ section_name, attribute_name, attribute_value }`
  // objects on the wire (same shape the curation detail response carries) — read it
  // directly into the structured shape rather than stringifying + re-parsing.
  let productDetails: ProductDetailAttribute[] | undefined;
  const rawDetails = fields.product_details;
  if (Array.isArray(rawDetails) && rawDetails.length > 0) {
    const first = rawDetails[0] as Record<string, unknown> | undefined;
    if (first && typeof first === 'object' && 'attribute_name' in first) {
      productDetails = (rawDetails as Record<string, unknown>[]).map((item) => ({
        sectionName: typeof item.section_name === 'string' ? item.section_name : '',
        attributeName: typeof item.attribute_name === 'string' ? item.attribute_name : '',
        attributeValue: typeof item.attribute_value === 'string' ? item.attribute_value : '',
      }));
    }
  }

  return productDetails ? { rawFields, productDetails } : { rawFields };
}
